import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface SectionProps {
  children: ReactNode
  className?: string
  variant?: 'default' | 'muted' | 'gradient' | 'bordered'
  containerClassName?: string
  id?: string
}

export function Section({
  children,
  className,
  variant = 'default',
  containerClassName,
  id,
}: SectionProps) {
  const variantStyles = {
    default: '',
    muted: 'bg-muted/50',
    gradient: 'gradient-hero',
    bordered: 'border-y border-border',
  }

  return (
    <section
      id={id}
      className={cn('section', variantStyles[variant], className)}
    >
      <div className={cn('container', containerClassName)}>
        {children}
      </div>
    </section>
  )
}

// Section Header Component
export interface SectionHeaderProps {
  title: string
  description?: string
  align?: 'left' | 'center' | 'right'
  className?: string
  titleClassName?: string
  descriptionClassName?: string
}

export function SectionHeader({
  title,
  description,
  align = 'center',
  className,
  titleClassName,
  descriptionClassName,
}: SectionHeaderProps) {
  const alignStyles = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }

  return (
    <div className={cn('mb-12', alignStyles[align], className)}>
      <h2 className={cn('text-3xl md:text-4xl font-bold text-foreground mb-4', titleClassName)}>
        {title}
      </h2>
      {description && (
        <p className={cn('text-lg text-muted-foreground max-w-2xl mx-auto', descriptionClassName, align === 'center' && 'mx-auto')}>
          {description}
        </p>
      )}
    </div>
  )
}

// Stats Section Component
export interface StatItemProps {
  value: string | number
  label: string
  change?: string
  positive?: boolean
  icon?: ReactNode
}

export function StatItem({ value, label, change, positive, icon }: StatItemProps) {
  return (
    <div className="text-center">
      {icon && (
        <div className="flex justify-center mb-2 text-primary">
          {icon}
        </div>
      )}
      <div className="text-3xl md:text-4xl font-bold text-foreground mb-2">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-muted-foreground mb-1">{label}</div>
      {change && (
        <div className={cn('text-sm font-medium', positive ? 'text-success' : 'text-destructive')}>
          {positive ? '+' : ''}{change}
        </div>
      )}
    </div>
  )
}

export interface StatsSectionProps {
  stats: StatItemProps[]
  className?: string
}

export function StatsSection({ stats, className }: StatsSectionProps) {
  return (
    <Section variant="muted" className={className}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {stats.map((stat, index) => (
          <StatItem key={index} {...stat} />
        ))}
      </div>
    </Section>
  )
}

// CTA Section Component
export interface CTASectionProps {
  title: string
  description: string
  primaryCTA?: {
    text: string
    onClick: () => void
  }
  secondaryCTA?: {
    text: string
    onClick: () => void
  }
  className?: string
}

export function CTASection({
  title,
  description,
  primaryCTA,
  secondaryCTA,
  className,
}: CTASectionProps) {
  return (
    <Section variant="gradient" className={className}>
      <div className="text-center max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
          {title}
        </h2>
        <p className="text-lg text-muted-foreground mb-8">
          {description}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {primaryCTA && (
            <button
              onClick={primaryCTA.onClick}
              className="btn btn-primary text-lg px-8 py-3"
            >
              {primaryCTA.text}
            </button>
          )}
          {secondaryCTA && (
            <button
              onClick={secondaryCTA.onClick}
              className="btn btn-outline text-lg px-8 py-3"
            >
              {secondaryCTA.text}
            </button>
          )}
        </div>
      </div>
    </Section>
  )
}
